// hooks/excerpts/mutations.js - React Query mutations for excerpts
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ExcerptAPI from 'apis/ExcerptAPI';
import QualAPI from 'utils/QualAPI';
import { userKeys } from 'hooks/users/queries';
import { excerptKeys } from './queries';
import { createCodeCacheUtils } from '../utils/codeCacheUtils';
import { normalizeId } from '../utils/idNormalization';
import { useOptimisticPromise } from '../optimistic/useOptimisticPromise';
// Import the utility functions from transcript queries
import { 
  addExcerptToParagraphs, 
  removeExcerptFromParagraphs, 
  replaceExcerptIdInParagraphs ,
  transcriptKeys
} from '../transcripts/queries';
import { codeKeys } from '../codes/queries';
import { surveyKeys } from '../surveys/queries';
// The shared settle body lives in a leaf module so the cable handler can
// import it without a cycle through this file; re-exported here for the
// existing consumers of this module's surface.
import { mergeServerExcerptIntoDetail, REMOVE_CODING_MUTATION_KEY } from './settleMerge';
export { mergeServerExcerptIntoDetail, REMOVE_CODING_MUTATION_KEY } from './settleMerge';

// The transcript cache entry carries excerpt MEMBERSHIP only (excerpt_ids
// — see the excerpt architecture notes in CLAUDE.md). Codings changes are
// detail-cache writes and never touch the transcript entry; only excerpt
// creation, deletion, and the optimistic→real id swap go through the
// membership writers in transcriptCacheUtils.
import {
  addExcerptIdToTranscript,
  removeExcerptIdFromTranscript,
  replaceExcerptIdInTranscript,
  findTranscriptIdsContainingExcerpt,
} from '../utils/transcriptCacheUtils';

// Helper function to get current user ID from React Query cache
const getCurrentUserId = (queryClient) => {
  const currentUser = queryClient.getQueryData(userKeys.current());
  return currentUser?.id || null;
};

// Cancel matching queries, returning the keys of those that were actually
// mid-fetch. Cancellation protects the optimistic write, but an aborted
// fetch does NOT restart on its own: with its observer still mounted, the
// cancelled query reverts and then sits idle — a filter report or list
// whose fetch was in flight at gesture time otherwise stays frozen until a
// reload (the filter panel's counts, in practice). onSettled restarts
// exactly these keys — never the whole root, which would refetch every
// excerpt list on every coding gesture.
const cancelQueriesTrackingAborted = async (queryClient, filters) => {
  const aborted = queryClient
    .getQueryCache()
    .findAll({ ...filters, fetchStatus: 'fetching' })
    .map((query) => query.queryKey);
  await queryClient.cancelQueries(filters);
  return aborted;
};

// The survey excerpt hooks (useSurveyResponseExcerpts /
// useSurveyQuestionExcerpts) seed the detail caches from their queryFns
// exactly like the excerpt lists, so a coding gesture must cancel their
// in-flight fetches too — otherwise a late response re-seeds pre-mutation
// codings (the seeding guard only fires for CANCELLED fetches). Scoped by
// predicate to the excerpt-bearing keys ([..., 'excerpts']) — the survey
// detail/rows/answer fetches seed nothing and must not pay an abort +
// restart per gesture.
const cancelSurveyExcerptFetches = (queryClient) =>
  cancelQueriesTrackingAborted(queryClient, {
    queryKey: surveyKeys.all,
    predicate: (query) => query.queryKey[query.queryKey.length - 1] === 'excerpts',
  });

const restartAbortedQueries = (queryClient, abortedQueryKeys) => {
  (abortedQueryKeys || []).forEach((queryKey) => {
    queryClient.invalidateQueries({ queryKey, exact: true });
  });
};

// Cancel the in-flight transcript refetch(es) whose queryFn would re-seed
// this excerpt's detail entry with pre-mutation codings: the one named by
// the detail probe, or — when no detail entry exists — the owner(s) found
// by scanning cached memberships (exact: excerpt ids are globally unique).
// Never every transcript's fetch.
// Returns the aborted keys (see cancelQueriesTrackingAborted).
const cancelOwningTranscriptFetches = async (queryClient, excerptId, probe) => {
  if (probe) {
    if (probe.transcript_id != null && probe.codeable_type === 'Transcript') {
      return cancelQueriesTrackingAborted(queryClient, { queryKey: transcriptKeys.detail(normalizeId(probe.transcript_id)) });
    }
    return [];
  }
  const owningIds = findTranscriptIdsContainingExcerpt(queryClient, excerptId);
  const aborted = [];
  for (const owningId of owningIds) {
    aborted.push(...await cancelQueriesTrackingAborted(queryClient, { queryKey: transcriptKeys.detail(owningId) }));
  }
  return aborted;
};

// One writer for "transform this excerpt's codings": a functional
// detail-cache update (reads the value current at write time, so
// overlapping mutations compose instead of overwriting each other).
// `transform(codings, excerpt)` returns the new codings array, or null for
// "no change".
const updateExcerptCodingsEverywhere = (queryClient, excerptId, transform) => {
  let updated = null;
  queryClient.setQueryData(excerptKeys.detail(excerptId), (current) => {
    if (!current) return current;
    const nextCodings = transform(current.codings || [], current);
    if (nextCodings === null) return current;
    updated = { ...current, codings: nextCodings };
    return updated;
  });
  // Under the ids-only model the detail cache IS everywhere: nothing
  // renders an excerpt that has no detail entry, so a missing entry means
  // nothing to update — the mutation's onSuccess writes the server's
  // excerpt into the detail cache and creates it then.
  return updated;
};

/**
 * Mutation for adding an excerpt with a code
 */
export function useCreateExcerpt() {
  const queryClient = useQueryClient();
  const cacheUtils = createCodeCacheUtils(queryClient);
  const { waitForPromise, setPromise } = useOptimisticPromise();
  
  return useMutation({
    mutationFn: async ({
      start,
      end,
      codeId,
      transcriptId,
      codeableType,
      userIds = [],
      text,
      client_id,
      trackingContext
    }) => {
      const resolvedCodeId = await waitForPromise(codeId);

      let transcriptVersion = null;

      if (codeableType === 'Transcript') {
        let transcriptData = queryClient.getQueryData(transcriptKeys.detail(normalizeId(transcriptId)));
        transcriptVersion = transcriptData?.version;
      }

      const promise = ExcerptAPI.addExcerpt(
        client_id,
        start,
        end,
        resolvedCodeId,
        transcriptId,
        codeableType,
        transcriptVersion,
        userIds,
        text,
        trackingContext
      ).then(response => response.data);

      // Store the promise to resolve the optimistic ID later
      setPromise(client_id, promise);
      return promise;
    },
    
    onMutate: async ({ codeId, start, end, transcriptId, codeableType = 'Transcript', text, client_id, userIds = [], onMutate }) => {
      // Normalize all IDs
      codeId = normalizeId(codeId);
      transcriptId = normalizeId(transcriptId);
      start = parseInt(start, 10);
      end = parseInt(end, 10);
      client_id = normalizeId(client_id); // Keep as-is (likely UUID)
      
      // Cancel relevant queries - but NOT the specific excerpt query we're about to create
      await cacheUtils.cancelQueries(codeId);

      // Only cancel list queries, not detail queries. Aborted fetches are
      // tracked and restarted in onSettled — see
      // cancelQueriesTrackingAborted.
      const abortedQueryKeys = [
        ...await cancelQueriesTrackingAborted(queryClient, { queryKey: excerptKeys.lists() }),
        ...await cancelQueriesTrackingAborted(queryClient, { queryKey: transcriptKeys.detail(transcriptId) }),
        // Survey-answer creates race the survey excerpt fetches the same
        // way transcript creates race the transcript refetch: a late
        // response's mapping pass would wipe the optimistic excerpt's
        // paragraph mapping.
        ...await cancelSurveyExcerptFetches(queryClient),
      ];
      
      // Create optimistic excerpt with temporary ID
      const currentUserId = getCurrentUserId(queryClient);
      
      const optimisticExcerpt = {
        id: client_id,
        start,
        end,
        text: text || '',
        transcript_id: transcriptId,
        codeable_type: codeableType,
        updated_at: new Date().toISOString(),
        codings: [{
          code_id: codeId,
          user_id: currentUserId,
          created_at: new Date().toISOString()
        }],
        memos: []
      };
      
      // Store the optimistic excerpt FIRST - before any query observer is
      // created, and before membership (the ordering invariant in
      // transcriptCacheUtils: detail entry exists before its id is a
      // member).
      queryClient.setQueryData(excerptKeys.detail(client_id), optimisticExcerpt);
      if (codeableType === 'Transcript') {
        addExcerptIdToTranscript(queryClient, transcriptId, client_id);
      }
      if ( onMutate ) onMutate();
      
      // Add excerpt to paragraph mappings using utility function
      const affectedParagraphs = addExcerptToParagraphs(
        queryClient, 
        transcriptId, 
        codeableType || 'Transcript', 
        client_id, 
        start, 
        end
      );

      // Get snapshot for rollback
      const codeSnapshot = await cacheUtils.getSnapshot(codeId);

      // Optimistically update code count
      if (codeSnapshot.previousCode) {
        cacheUtils.updateCode(codeId, { 
          count: (codeSnapshot.previousCode.count || 0) + 1 
        });
      }

      return {
        //codeSnapshot,
        optimisticExcerpt,
        affectedParagraphs,
        client_id,
        abortedQueryKeys
      };
    },
    
    onError: (error, { codeId, transcriptId, codeableType = 'Transcript' }, context) => {
      codeId = normalizeId(codeId);
      transcriptId = normalizeId(transcriptId);
      
      queryClient.invalidateQueries({ queryKey: codeKeys.lists()});
      
      if (context?.codeSnapshot) {
        cacheUtils.restoreFromSnapshot(codeId, context.codeSnapshot);
      }
      
      if (context?.client_id) {
        queryClient.removeQueries({ 
          queryKey: excerptKeys.detail(context.client_id) 
        });
      }
      
      if (context?.affectedParagraphs && context?.client_id) {
        removeExcerptFromParagraphs(
          queryClient,
          transcriptId,
          codeableType,
          context.client_id,
          context.affectedParagraphs
        );
      }

      if (context?.client_id) {
        removeExcerptIdFromTranscript(queryClient, transcriptId, context.client_id);
      }
    },
    
    onSuccess: (response, variables, context) => {
      const { codeId, transcriptId, codeableType = 'Transcript' } = variables;
      const normalizedCodeId = normalizeId(codeId);
      const normalizedTranscriptId = normalizeId(transcriptId);
      const actualExcerpt = response.excerpt;

      if (actualExcerpt && context?.client_id) {
        // Settle through the shared merge, not a raw write: the server
        // can REUSE an existing excerpt for an identical span
        // (find_or_create_by), so this response is subject to the same
        // reorder/pending-removal races as add/remove settles — a raw
        // overwrite resurrected a concurrently removed coding.
        const normalizedExcerptId = normalizeId(actualExcerpt.id);
        mergeServerExcerptIntoDetail(queryClient, actualExcerpt);
        
        // Remove the optimistic entry
        queryClient.removeQueries({ 
          queryKey: excerptKeys.detail(context.client_id) 
        });
        
        replaceExcerptIdInParagraphs(
          queryClient,
          normalizedTranscriptId,
          codeableType,
          context.client_id,
          normalizedExcerptId,
          context.affectedParagraphs
        );
        
        // Swap the optimistic id for the server's in the transcript's
        // membership (the real detail entry was written above — ordering
        // invariant). The writer dedupes, covering the server reusing an
        // EXISTING excerpt for an identical span (find_or_create_by in
        // excerpts_with_codes_controller).
        if (codeableType === 'Transcript') {
          replaceExcerptIdInTranscript(queryClient, normalizedTranscriptId, context.client_id, normalizedExcerptId);
        }
      }
      
      if (response?.code && typeof response.code.count !== 'undefined') {
        cacheUtils.updateCode(normalizedCodeId, { count: response.code.count });
      }
    },

    onSettled: (response, error, variables, context) => {
      restartAbortedQueries(queryClient, context?.abortedQueryKeys);
    }
  });
}

/**
 * Mutation for adding a code to an excerpt
 */
export function useAddCodeToExcerpt() {
  const queryClient = useQueryClient();
  const cacheUtils = createCodeCacheUtils(queryClient);
  const { waitForPromise } = useOptimisticPromise();
  
  return useMutation({
    mutationFn: async ({ excerptId, codeId, userIds = [], trackingContext }) => {
      const resolvedCodeId = await waitForPromise(codeId);
      const resolvedExcerptId = await waitForPromise(excerptId);
      return ExcerptAPI.addCodeToExcerpt(resolvedCodeId, resolvedExcerptId, userIds, trackingContext)
    },
    
    onMutate: async ({ excerptId, codeId }) => {
      // Normalize IDs
      excerptId = normalizeId(excerptId);
      codeId = normalizeId(codeId);
      
      // This one is more complex because it updates multiple entities
      await cacheUtils.cancelQueries(codeId);
      // Cancel EVERY in-flight excerpt query (the detail entry AND the
      // filtered/code/bulk lists, which all share this root): a list
      // response resolving after this mutation would re-seed the detail
      // caches and the bulk copy with pre-mutation codings via the
      // list-seeded sync in hooks/excerpts/queries.js. Aborted fetches are
      // tracked and restarted in onSettled — see
      // cancelQueriesTrackingAborted.
      const abortedQueryKeys = await cancelQueriesTrackingAborted(queryClient, { queryKey: excerptKeys.all });
      abortedQueryKeys.push(...await cancelSurveyExcerptFetches(queryClient));

      const codeSnapshot = await cacheUtils.getSnapshot(codeId);

      // Probe only to decide which transcript refetch to cancel — the
      // actual read-modify-write happens AFTER every await, functionally.
      const probe = queryClient.getQueryData(excerptKeys.detail(excerptId));
      abortedQueryKeys.push(...await cancelOwningTranscriptFetches(queryClient, excerptId, probe));

      const optimisticCoding = {
        code_id: codeId,
        user_id: getCurrentUserId(queryClient),
        created_at: new Date().toISOString(),
        // Local-only identity for the error rollback. code_id can be
        // rewritten under us (optimistic UUID → real id via
        // resolveOptimisticId) and created_at collides for two adds in the
        // same millisecond, but this key survives the rewrite (spread copy)
        // and is unique per mutation. Server data replaces the coding on
        // success, so the field never outlives the optimistic window.
        optimistic_key: `${Date.now()}-${Math.random().toString(36).slice(2)}`
      };

      updateExcerptCodingsEverywhere(queryClient, excerptId, (codings) => [...codings, optimisticCoding]);

      // Update code count
      if (codeSnapshot.previousCode) {
        cacheUtils.updateCode(codeId, {
          count: (codeSnapshot.previousCode.count || 0) + 1
        });
      }

      return { codeSnapshot, optimisticCoding, abortedQueryKeys };
    },

    onError: (err, { excerptId }, context) => {
      const normalizedExcerptId = normalizeId(excerptId);

      // invalidate list caches to force refetch
      queryClient.invalidateQueries({ queryKey: codeKeys.lists()});

      // Roll back by removing exactly the failed coding from the CURRENT
      // cache values, not by restoring a snapshot: a snapshot predates any
      // concurrent mutation on the same excerpt and would erase its coding
      // from the shared detail cache.
      //
      // Matched on the coding's local optimistic_key — NOT code_id: when
      // the code was created in the same gesture, resolveOptimisticId
      // rewrites the cached coding's optimistic UUID to the real id before
      // this error can land, and a code_id match would no-op, stranding the
      // rejected coding. The key survives that rewrite (spread copy) and,
      // unlike created_at, is unique even for two adds in one millisecond.
      if (context?.optimisticCoding) {
        const { optimistic_key } = context.optimisticCoding;
        updateExcerptCodingsEverywhere(queryClient, normalizedExcerptId, (codings) =>
          codings.filter((c) => c.optimistic_key !== optimistic_key)
        );
      }

      console.error('Error adding code to excerpt:', err);
    },

    onSuccess: (data, variables, context) => {
      // Write the server's excerpt (with its codings) into the ground-truth
      // detail cache — creating the entry when onMutate had none to update
      // (e.g. bot-coded excerpts opened straight into the AI coding modal).
      // Nothing else writes this coding back on the acting client — the
      // ActionCable broadcast is keyed to exclude the originating session.
      // This also swaps the optimistic coding for the server's version.
      // Keyed off the SERVER's excerpt id inside the shared merge — the
      // caller may hold a create's optimistic UUID (add fired before the
      // create settled), and writing under that dead key would leave the
      // real entry — the only render source under the ids-only model —
      // stale until a refetch.
      if (data?.excerpt?.id) {
        mergeServerExcerptIntoDetail(queryClient, data.excerpt);
        // The optimistic marker's lifetime must equal the mutation's
        // in-flight window, and this settle ends the window. The merge
        // usually replaces the optimistic coding with the server's version,
        // but not on the remove-then-readd edge (a pending removal filters
        // this coding OUT of the server list, so keepLocal retains the
        // optimistic copy) — and a stray marker is not cosmetic: keepLocal
        // in a LATER merge would preserve the coding against a genuine
        // server-side removal. Strip exactly this mutation's marker,
        // keeping the coding itself.
        if (context?.optimisticCoding) {
          const { optimistic_key } = context.optimisticCoding;
          updateExcerptCodingsEverywhere(queryClient, normalizeId(data.excerpt.id), (codings) => {
            const index = codings.findIndex((c) => c.optimistic_key === optimistic_key);
            if (index === -1) return null;
            const next = [...codings];
            const { optimistic_key: _stripped, ...settled } = next[index];
            next[index] = settled;
            return next;
          });
        }
      }
    },

    onSettled: (data, error, variables, context) => {
      restartAbortedQueries(queryClient, context?.abortedQueryKeys);
    }
  });
}

/**
 * Mutation for removing a code from an excerpt
 */
export const useRemoveCodeFromExcerpt = () => {
  const queryClient = useQueryClient();
  const cacheUtils = createCodeCacheUtils(queryClient);
  const { waitForPromise } = useOptimisticPromise();

  return useMutation({
    // The key lets mergeServerExcerptIntoDetail see in-flight removals and
    // keep a reordered response from resurrecting their codings.
    mutationKey: REMOVE_CODING_MUTATION_KEY,
    mutationFn: async ({ codeId, excerptId, userIds = [], trackingContext }) => {
      // Toggling a code off right after adding it can run before the create
      // responses land; resolve optimistic ids the same way useAddCodeToExcerpt
      // does instead of DELETEing against a client uuid.
      const resolvedCodeId = await waitForPromise(codeId);
      const resolvedExcerptId = await waitForPromise(excerptId);
      // The DELETE response carries the full post-removal excerpt
      // (api/excerpts/destroy.json.jbuilder) — onSuccess writes it back so
      // the removal lands even when onMutate had no detail entry to update
      // (an invariant violation — see reportMissingExcerptDetails — but
      // one this response can still heal). Discarding it here silently
      // lost the removal in cache for exactly that case.
      return QualAPI.deleteCodeFromExcerpt(
        resolvedCodeId,
        resolvedExcerptId,
        userIds,
        trackingContext
      );
    },
    
    onMutate: async ({ codeId, excerptId, userIds = [] }) => {
      // Normalize IDs
      codeId = normalizeId(codeId);
      excerptId = normalizeId(excerptId);
      userIds = userIds.map(id => normalizeId(id)).filter(id => id !== null);
      
      // Cancel relevant queries
      await cacheUtils.cancelQueries(codeId);
      // Cancel EVERY in-flight excerpt query (the detail entry AND the
      // filtered/code/bulk lists, which all share this root): a list
      // response resolving after this mutation would re-seed the detail
      // caches and the bulk copy with pre-mutation codings via the
      // list-seeded sync in hooks/excerpts/queries.js. Aborted fetches are
      // tracked and restarted in onSettled — see
      // cancelQueriesTrackingAborted.
      const abortedQueryKeys = await cancelQueriesTrackingAborted(queryClient, { queryKey: excerptKeys.all });
      abortedQueryKeys.push(...await cancelSurveyExcerptFetches(queryClient));

      // Get snapshots
      const codeSnapshot = await cacheUtils.getSnapshot(codeId);

      // Remove codings that match the code_id and are from the specified users
      const matchesRemoval = (coding) => {
        const matchesCode = coding.code_id === codeId;
        const matchesUser = !userIds.length || userIds.includes(coding.user_id);
        return matchesCode && matchesUser;
      };

      // Probe only for ownership; the read-modify-write happens AFTER
      // every await, functionally (see updateExcerptCodingsEverywhere).
      // hadDetailEntry also scopes onSuccess's count heal to the
      // missing-entry window.
      const probe = queryClient.getQueryData(excerptKeys.detail(excerptId));
      const hadDetailEntry = probe !== undefined;
      abortedQueryKeys.push(...await cancelOwningTranscriptFetches(queryClient, excerptId, probe));

      // Remove codings optimistically; keep the removed ones so an error
      // rollback can re-add exactly these codings.
      let removedCodings = [];
      updateExcerptCodingsEverywhere(queryClient, excerptId, (codings) => {
        removedCodings = codings.filter(matchesRemoval);
        return codings.filter((coding) => !matchesRemoval(coding));
      });

      // Update code count optimistically (decrease by number of removed codings)
      if (codeSnapshot.previousCode && removedCodings.length > 0) {
        cacheUtils.updateCode(codeId, {
          count: Math.max(0, (codeSnapshot.previousCode.count || 0) - removedCodings.length)
        });
      }

      return { codeSnapshot, removedCodings, abortedQueryKeys, hadDetailEntry };
    },

    onError: (error, variables, context) => {
      const { excerptId, codeId } = variables;
      const normalizedExcerptId = normalizeId(excerptId);
      const normalizedCodeId = normalizeId(codeId);

      // Roll back by re-adding exactly the codings this mutation removed,
      // onto the CURRENT cache values — a snapshot restore would erase any
      // concurrent mutation's coding from the shared caches. Idempotent: a
      // coding a concurrent write already restored (a cable broadcast, or
      // another mutation's onSuccess server snapshot) is skipped rather
      // than duplicated — codings are unique per (code, user) server-side.
      if (context?.removedCodings?.length) {
        updateExcerptCodingsEverywhere(queryClient, normalizedExcerptId, (codings) => {
          const missing = context.removedCodings.filter(
            (removed) => !codings.some((c) => c.code_id === removed.code_id && c.user_id === removed.user_id)
          );
          return missing.length ? [...codings, ...missing] : null;
        });
      }

      // Restore code from snapshot
      if (context?.codeSnapshot) {
        cacheUtils.restoreFromSnapshot(normalizedCodeId, context.codeSnapshot);
      }

      console.error('Error removing code from excerpt:', error);
    },
    
    onSuccess: (data, variables, context) => {
      // Settle the server's post-removal excerpt through the shared merge,
      // creating the entry when onMutate had none to update (without this,
      // a removal against a missing entry stayed rendered until the next
      // refetch).
      if (data?.excerpt?.id) {
        mergeServerExcerptIntoDetail(queryClient, data.excerpt);
      }
      // Count heal, scoped to the genuinely-missing-entry window: with no
      // detail entry, onMutate had no codings to count, so the optimistic
      // decrement was skipped. hadDetailEntry distinguishes that from a
      // common zero-match race (double-fired uncode, collaborator's
      // removal landing first), which must not pay a full code-list
      // refetch per gesture.
      if (data?.excerpt && context?.hadDetailEntry === false) {
        queryClient.invalidateQueries({ queryKey: codeKeys.lists() });
      }
    },

    onSettled: (data, error, variables, context) => {
      restartAbortedQueries(queryClient, context?.abortedQueryKeys);
    }
  });
};

// Export all excerpt-related mutations
export const excerptMutations = {
  useCreateExcerpt,
  useAddCodeToExcerpt,
  useRemoveCodeFromExcerpt
};

// Export cable-related mutations
export { useUpdateExcerptFromCable } from './mutations/useUpdateExcerptFromCable';